import { getPartnerBySlug, isPartnerEnabled } from '@/config/partners';
import { createContext, useContext } from 'react';

const PARTNER_STORAGE_KEY = 'heidi_partner';

export interface WhiteLabelContextValue {
  /** Whether the app is currently in white-label mode */
  isWhiteLabel: boolean;
  /** The partner's display name (e.g., "R1"), or null if not white-labeled */
  partnerName: string | null;
  /** URL to the partner's logo, or null if not white-labeled */
  partnerLogoUrl: string | null;
  /** The app name to display - partner name if white-labeled, "Heidi" otherwise */
  appName: string;
}

export const WhiteLabelContext = createContext<WhiteLabelContextValue | undefined>(
  undefined
);

/**
 * Get the initial partner slug from localStorage (client-side only).
 */
export function getStoredPartnerSlug(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(PARTNER_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Store partner slug in localStorage.
 */
export function setPartnerSlug(slug: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(PARTNER_STORAGE_KEY, slug);
  } catch {
    // Ignore storage write failures in restricted environments.
  }
}

/**
 * Clear partner slug from localStorage (e.g., on logout).
 */
export function clearPartnerSlug(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(PARTNER_STORAGE_KEY);
  } catch {
    // Ignore storage delete failures in restricted environments.
  }
}

/**
 * Hook to access white-label branding context.
 *
 * @example
 * ```tsx
 * const { isWhiteLabel, partnerName, appName } = useWhiteLabel();
 *
 * return (
 *   <h1>Welcome to {appName}</h1>
 * );
 * ```
 */
export function useWhiteLabel(): WhiteLabelContextValue {
  const context = useContext(WhiteLabelContext);

  if (context === undefined) {
    throw new Error('useWhiteLabel must be used within a WhiteLabelProvider');
  }

  return context;
}

/**
 * Hook to safely access white-label context outside of provider.
 * Returns default values if provider is not present.
 * Useful for components that may render before provider is mounted.
 */
export function useWhiteLabelSafe(): WhiteLabelContextValue {
  const context = useContext(WhiteLabelContext);

  if (context === undefined) {
    // Fallback: read directly from localStorage and check feature flag
    const storedSlug = getStoredPartnerSlug();
    const partner = storedSlug ? getPartnerBySlug(storedSlug) : null;
    const isEnabled = storedSlug ? isPartnerEnabled(storedSlug) : false;

    if (partner && isEnabled) {
      return {
        isWhiteLabel: true,
        partnerName: partner.name,
        partnerLogoUrl: partner.logoUrl,
        appName: partner.name,
      };
    }
    return {
      isWhiteLabel: false,
      partnerName: null,
      partnerLogoUrl: null,
      appName: 'Heidi',
    };
  }

  return context;
}
